
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CalendarEvent } from '../types';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { useQueryClient } from '@tanstack/react-query';

export const useShiftApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLanguage();
  const { isAdmin, isManager, user } = useUser();
  const queryClient = useQueryClient();
  
  const checkPermissions = async (teamId: string): Promise<boolean> => {
    console.log('=== CHECKING PERMISSIONS ===');
    console.log('User ID:', user?.id);
    console.log('Team ID:', teamId);
    console.log('isAdmin from useUserRoles:', isAdmin);
    console.log('isManager from useUserRoles:', isManager);
    
    if (!user) {
      console.log('No user found');
      return false;
    }

    try {
      // Check admin status directly from database first
      const { data: adminCheck, error: adminError } = await supabase
        .rpc('is_admin', { user_id: user.id });
      
      console.log('Direct admin check result:', adminCheck);
      
      if (adminError) {
        console.error('Error checking admin status:', adminError);
      } else if (adminCheck === true) {
        console.log('User is admin - allowing operation');
        return true;
      }

      // Check manager status if not admin
      const { data: managerCheck, error: managerError } = await supabase
        .rpc('is_manager', { user_id: user.id });
      
      console.log('Direct manager check result:', managerCheck);
      
      if (managerError) {
        console.error('Error checking manager status:', managerError);
      } else if (managerCheck === true) {
        console.log('User is manager - checking team access');
        
        // First try the RPC function
        const { data: teamAccess, error: teamAccessError } = await supabase
          .rpc('manager_has_team_access', {
            user_id: user.id,
            team_id: teamId
          });
        
        console.log('Manager team access result:', teamAccess);
        
        if (teamAccessError) {
          console.error('Error checking team access via RPC, trying direct query');
          
          // Fallback to direct table query
          const { data: directAccess, error: directError } = await supabase
            .from('manager_teams')
            .select('id')
            .eq('manager_id', user.id)
            .eq('team_id', teamId)
            .limit(1);
            
          if (directError) {
            console.error('Error with direct manager_teams query:', directError);
            return false;
          }
          
          const hasDirectAccess = directAccess && directAccess.length > 0;
          console.log('Direct manager_teams query result:', directAccess);
          console.log('Has direct access:', hasDirectAccess);
          return hasDirectAccess;
        }
        
        return teamAccess === true;
      }

      console.log('User is neither admin nor manager with access');
      return false;
      
    } catch (error) {
      console.error('Exception in permission check:', error);
      
      // Final fallback: if we have isAdmin or isManager from context, use that
      if (isAdmin) {
        console.log('Using cached admin status as fallback');
        return true;
      }
      
      if (isManager) {
        console.log('Using cached manager status, checking direct table access');
        try {
          const { data: fallbackAccess, error: fallbackError } = await supabase
            .from('manager_teams')
            .select('id')
            .eq('manager_id', user.id)
            .eq('team_id', teamId)
            .limit(1);
            
          if (!fallbackError && fallbackAccess && fallbackAccess.length > 0) {
            console.log('Fallback manager check succeeded');
            return true;
          }
        } catch (fallbackException) {
          console.error('Fallback permission check failed:', fallbackException);
        }
      }
      
      return false;
    }
  };
  
  const createShift = async (shift: CalendarEvent): Promise<CalendarEvent | null> => {
    setIsLoading(true);
    try {
      console.log('=== CREATING SHIFT ===');
      console.log('Creating shift for team:', shift.team_id, 'user:', user?.id);
      
      // Check permissions for the team
      const hasPermission = await checkPermissions(shift.team_id);
      console.log('Has permission to create shift:', hasPermission);
      
      if (!hasPermission) {
        console.log('Permission denied - insufficient access');
        toast.error('You do not have permission to create shifts for this team');
        return null;
      }

      // Remove any properties that shouldn't be sent to the API
      const { guard_names, id, ...shiftData } = shift;
      
      // Ensure we have a new ID and proper user context
      const newShift = {
        ...shiftData,
        id: crypto.randomUUID(),
        created_by: user?.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log('Attempting to insert shift:', newShift);
      
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(newShift)
        .select('*')
        .single();
        
      if (error) {
        console.error('Error inserting shift:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        if (error.message.includes('row-level security policy')) {
          console.error('RLS POLICY VIOLATION - User cannot insert shifts');
          toast.error('Permission denied: Unable to create shift due to security policy');
        } else {
          toast.error(`Error creating shift: ${error.message}`);
        }
        throw error;
      }
      
      console.log('Successfully created shift:', data);
      toast.success(t('shiftCreated') || 'Shift created');
      
      // Log will be automatically created by database trigger
      console.log('Shift creation will be logged automatically by database trigger');
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return data as CalendarEvent;
      
    } catch (error) {
      console.error('Error creating shift:', error);
      if (!error.message?.includes('row-level security policy')) {
        toast.error(t('errorCreatingShift') || 'Error creating shift');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateShift = async (shiftId: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent | null> => {
    setIsLoading(true);
    try {
      console.log('=== UPDATING SHIFT ===');
      console.log('Updating shift ID:', shiftId);
      console.log('Updates:', updates);
      console.log('User ID:', user?.id);

      // If we have a team_id in updates, check permissions
      if (updates.team_id) {
        const hasPermission = await checkPermissions(updates.team_id);
        console.log('Has permission to update shift:', hasPermission);
        
        if (!hasPermission) {
          console.log('Permission denied - insufficient access');
          toast.error('You do not have permission to update shifts for this team');
          return null;
        }
      }

      // Remove properties that shouldn't be updated
      const { guard_names, id, created_by, created_at, ...updateData } = updates;
      
      // Add updated timestamp
      const finalUpdates = {
        ...updateData,
        updated_at: new Date().toISOString()
      };

      console.log('Final updates to apply:', finalUpdates);

      // Ensure assigned_guards is always an array
      if (finalUpdates.assigned_guards && !Array.isArray(finalUpdates.assigned_guards)) {
        console.log('Converting assigned_guards to array:', finalUpdates.assigned_guards);
        finalUpdates.assigned_guards = [finalUpdates.assigned_guards];
      }

      console.log('Final updates to apply (after array check):', finalUpdates);

      const { data, error } = await supabase
        .from('team_schedules')
        .update(finalUpdates)
        .eq('id', shiftId)
        .select('*')
        .maybeSingle();
      
      if (error) {
        console.error('Error updating shift:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        if (error.message.includes('row-level security policy')) {
          toast.error('Permission denied: Unable to update shift due to security policy');
        } else {
          toast.error(`Error updating shift: ${error.message}`);
        }
        throw error;
      }
      
      if (!data) {
        console.error('No shift found to update or permission denied');
        toast.error('Shift not found or you do not have permission to update it');
        return null;
      }
      
      console.log('Successfully updated shift:', data);
      toast.success(t('shiftUpdated') || 'Shift updated');
      
      // Immediately invalidate and refetch schedules using the query client
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['all-schedules-for-conflict-check'] });
      await queryClient.refetchQueries({ queryKey: ['schedules'] });
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return data as CalendarEvent;
    } catch (error) {
      console.error('Error updating shift:', error);
      if (!error.message?.includes('row-level security policy')) {
        toast.error(t('errorUpdatingShift') || 'Error updating shift');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  const deleteShift = async (shiftId: string, teamId?: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // If teamId is provided, check permissions
      if (teamId) {
        const hasPermission = await checkPermissions(teamId);
        if (!hasPermission) {
          toast.error('You do not have permission to delete shifts for this team');
          return false;
        }
      }

      // First get the shift data before deleting for logging
      const { data: shiftData, error: fetchError } = await supabase
        .from('team_schedules')
        .select('*')
        .eq('id', shiftId)
        .single();

      if (fetchError) {
        console.error('Error fetching shift data before deletion:', fetchError);
      } else {
        console.log('Fetched shift data for deletion logging:', shiftData);
      }

      const { error } = await supabase
        .from('team_schedules')
        .delete()
        .eq('id', shiftId);
      
      if (error) throw error;
      
      console.log('Shift deleted successfully, now logging the change');
      toast.success(t('shiftDeleted') || 'Shift deleted');
      
      // Log will be automatically created by database trigger
      console.log('Shift deletion will be logged automatically by database trigger');
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return true;
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.error(t('errorDeletingShift') || 'Error deleting shift');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const deleteBulkShifts = async (shiftIds: string[], teamId?: string): Promise<boolean> => {
    if (!shiftIds || shiftIds.length === 0) {
      return true; // Nothing to delete
    }
    
    setIsLoading(true);
    try {
      // If teamId is provided, check permissions
      if (teamId) {
        const hasPermission = await checkPermissions(teamId);
        if (!hasPermission) {
          toast.error('You do not have permission to delete shifts for this team');
          return false;
        }
      }

      const { error, count } = await supabase
        .from('team_schedules')
        .delete({ count: 'exact' })
        .in('id', shiftIds);
      
      if (error) throw error;
      
      toast.success(
        t('shiftsDeleted') || 'Shifts deleted', 
        { description: `${count || shiftIds.length} ${t('shiftsRemoved') || 'shifts removed'}` }
      );
      
      // Trigger a refetch of schedules to update the UI
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return true;
    } catch (error) {
      console.error('Error bulk deleting shifts:', error);
      toast.error(t('errorDeletingShifts') || 'Error deleting shifts');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    createShift,
    updateShift,
    deleteShift,
    deleteBulkShifts,
    isLoading
  };
};
